import {IssueRelativeTime} from '@github-ui/ago'
import {SlackIcon} from '@github-ui/custom-icons/SlackIcon'
import {matchesFocusVisible, useFocusVisibleWithin} from '@github-ui/focus-visible'
import {useHoverPopover} from '@github-ui/use-hover-popover'
import {ChecklistIcon, FileIcon, KebabHorizontalIcon, LinkIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, IconButton, Link, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type RefObject, useCallback, useRef, useState} from 'react'

import type {PanelArtifact} from './ArtifactPanelContent'
import styles from './ArtifactRow.module.css'
import type {EditableArtifact} from './create-artifact-dialog-types'
import {safeHref} from './safe-href'
import type {IssueArtifact} from './types'

function ArtifactTypeIcon({type, body}: {type: IssueArtifact['type']; body: string}) {
  const isSlackLink = type === 'link' && isSlackUrl(body)
  const Icon = type === 'plan' ? ChecklistIcon : type === 'link' ? (isSlackLink ? SlackIcon : LinkIcon) : FileIcon
  return (
    <span className={styles.IconWrap} data-testid={isSlackLink ? 'slack-icon' : undefined}>
      {isSlackLink ? <SlackIcon /> : <Icon size={14} className={styles.Icon} />}
    </span>
  )
}

function isSlackUrl(value: string): boolean {
  try {
    const url = new URL(value, 'https://github.com')
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      (url.hostname === 'slack.com' || url.hostname.endsWith('.slack.com'))
    )
  } catch {
    return false
  }
}

/** Strips protocol and trailing slash so a link's URL reads cleanly as a title. */
function displayUrl(url: string): string {
  return url
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '')
}

type PersistedArtifactRowProps = {
  id: number
  number: number
  type: IssueArtifact['type']
  name: string
  body: string
  bodyHTML?: string
  updatedAt?: string
  updatedByActor?: IssueArtifact['updatedByActor']
  onDelete?: (id: number) => void
  onEdit?: (artifact: EditableArtifact, returnFocusRef: RefObject<HTMLButtonElement | null>) => void
  onSelect?: (artifact: PanelArtifact) => void
}

export const PersistedArtifactRow = memo(function PersistedArtifactRowImpl({
  id,
  number,
  type,
  name,
  body,
  bodyHTML,
  updatedAt,
  updatedByActor,
  onDelete,
  onEdit,
  onSelect,
}: PersistedArtifactRowProps) {
  const actionsButtonRef = useRef<HTMLButtonElement>(null)
  const linkRef = useRef<HTMLAnchorElement>(null)
  const rowRef = useRef<HTMLLIElement>(null)
  const {open: linkPopoverOpen, anchorRegionProps, anchorProps, contentProps, closeNow} = useHoverPopover()
  const isLink = type === 'link'
  const cleanedUrl = isLink && body ? displayUrl(body) : undefined

  // For links, fall back to the cleaned URL as the title when there's no
  // meaningful name (empty, or the name is just the URL itself).
  const trimmedName = name.trim()
  const linkTitleIsUrl = isLink && (!trimmedName || trimmedName === body.trim() || trimmedName === cleanedUrl)
  const displayName = linkTitleIsUrl ? (cleanedUrl ?? name) : name

  // `menuOpen` and `titleFocusVisible` replace `:has()` selectors on `.Row` (menu-open,
  // title focus-visible) to avoid WebKit `:has()` style invalidation across the artifact list.
  // The actions reveal uses the shared `useFocusVisibleWithin` helper, which toggles
  // `data-focus-visible-within` on the actions container via delegated listeners (no re-render).
  const [menuOpen, setMenuOpen] = useState(false)
  const [titleFocusVisible, setTitleFocusVisible] = useState(false)
  const actionsFocusVisibleRef = useFocusVisibleWithin()

  const handleTitleFocus = useCallback((event: React.FocusEvent<HTMLElement>) => {
    setTitleFocusVisible(matchesFocusVisible(event.currentTarget))
  }, [])
  const handleTitleBlur = useCallback(() => setTitleFocusVisible(false), [])
  const handleLinkFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      anchorProps.onFocus()
      handleTitleFocus(event)
    },
    [anchorProps, handleTitleFocus],
  )
  const handleLinkBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      anchorProps.onBlur(event)
      handleTitleBlur()
    },
    [anchorProps, handleTitleBlur],
  )
  const handleClick = useCallback(() => {
    onSelect?.({
      id: `${type}:${id}`,
      type,
      name,
      body,
      bodyHTML: bodyHTML ?? '',
      updatedAt,
      updatedByActor,
    })
  }, [id, type, name, body, bodyHTML, updatedAt, updatedByActor, onSelect])

  const handleDelete = useCallback(() => {
    onDelete?.(id)
  }, [id, onDelete])

  const handleEdit = useCallback(() => {
    onEdit?.({number, type, name, body}, actionsButtonRef)
  }, [body, name, number, onEdit, type])

  const linkHref = isLink ? safeHref(body) : undefined

  return (
    <li
      ref={rowRef}
      className={clsx(styles.Row, menuOpen && styles.RowMenuOpen, titleFocusVisible && styles.RowTitleFocusVisible)}
    >
      <ArtifactTypeIcon type={type} body={body} />
      <div className={styles.Content} {...(isLink ? anchorRegionProps : {})}>
        {isLink && linkHref ? (
          <Link
            {...anchorProps}
            ref={linkRef}
            className={styles.TitleButton}
            href={linkHref}
            target="_blank"
            rel="noopener noreferrer"
            onFocus={handleLinkFocus}
            onBlur={handleLinkBlur}
          >
            <span className={styles.Title}>{displayName}</span>
            <VisuallyHidden>
              <span>Full URL: {body}.</span>
              {updatedAt ? (
                <span>
                  {' '}
                  Updated on <time dateTime={updatedAt}>{updatedAt}</time>
                  {updatedByActor ? <> by {updatedByActor.login}.</> : <>.</>}
                </span>
              ) : null}
            </VisuallyHidden>
          </Link>
        ) : (
          <button
            type="button"
            className={styles.TitleButton}
            aria-label={displayName}
            onClick={handleClick}
            onFocus={handleTitleFocus}
            onBlur={handleTitleBlur}
          >
            <span className={styles.Title}>{displayName}</span>
          </button>
        )}
        {isLink && linkHref && linkPopoverOpen ? (
          <AnchoredOverlay
            open={linkPopoverOpen}
            anchorRef={rowRef}
            renderAnchor={null}
            onClose={closeNow}
            side="outside-bottom"
            align="start"
            focusTrapSettings={{disabled: true}}
            focusZoneSettings={{disabled: true}}
            overlayProps={{preventFocusOnOpen: true, returnFocusRef: linkRef}}
          >
            <div
              {...contentProps}
              aria-hidden="true"
              className={styles.LinkPopover}
              data-testid="link-artifact-popover"
            >
              <span className={styles.LinkPopoverUrl}>{body}</span>
              {updatedAt ? (
                <span className={styles.LinkPopoverMetadata}>
                  Updated <IssueRelativeTime datetime={updatedAt} />
                  {updatedByActor ? <> by {updatedByActor.login}</> : null}
                </span>
              ) : null}
            </div>
          </AnchoredOverlay>
        ) : null}
      </div>
      <div className={styles.HoverActions} ref={actionsFocusVisibleRef}>
        <ActionMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <ActionMenu.Anchor>
            <IconButton
              ref={actionsButtonRef}
              icon={KebabHorizontalIcon}
              variant="invisible"
              size="small"
              aria-label={`More actions for ${displayName}`}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item onSelect={handleEdit}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
              <ActionList.Item variant="danger" onSelect={handleDelete}>
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </li>
  )
})
